import type { LucideIcon } from 'lucide-react';
import { Activity, BarChart2, BookOpen, Building2, Code2, Database, GitBranch, HelpCircle, LayoutDashboard, Palette, ShieldCheck, Sparkles, Users, Wrench } from 'lucide-react';
export const ADMIN_ROUTE_IDS = ['dashboard', 'users', 'schools', 'question-bank', 'governance', 'ssot', 'code-registry', 'design-system', 'support-content', 'ldg-graph', 'mascot', 'diagnostics', 'logs', 'settings', 'analytics'] as const;
export type AdminRoute = (typeof ADMIN_ROUTE_IDS)[number];
export type AdminNavigationContext = {
  kind: 'question-bank-question';
  familyId: string;
  questionId: string;
} | {
  kind: 'governance-question';
  questionId: string;
};
export interface AdminNavItem {
  id: AdminRoute;
  label: string;
  description: string;
  icon: LucideIcon;
}
export interface AdminNavGroup {
  label: string;
  items: readonly AdminNavItem[];
}
export const ADMIN_ROUTE_META: Record<AdminRoute, AdminNavItem> = {
  dashboard: {
    id: 'dashboard',
    label: 'Dashboard',
    description: 'Systeem overzicht',
    icon: LayoutDashboard
  },
  users: {
    id: 'users',
    label: 'Gebruikers',
    description: 'Alle accounts beheren',
    icon: Users
  },
  schools: {
    id: 'schools',
    label: 'Scholen',
    description: 'Register & aanvragen',
    icon: Building2
  },
  'question-bank': {
    id: 'question-bank',
    label: 'Vragenbank',
    description: 'Alle vragen & seeds',
    icon: BookOpen
  },
  governance: {
    id: 'governance',
    label: 'Governance & Kwaliteit',
    description: 'Pipeline, validatie & quality workspace',
    icon: ShieldCheck
  },
  ssot: {
    id: 'ssot',
    label: 'SSOT Registry',
    description: 'Live single-sources-of-truth',
    icon: Database
  },
  'code-registry': {
    id: 'code-registry',
    label: 'Code Registry',
    description: 'Issue-code frequentie & legacy',
    icon: Code2
  },
  'design-system': {
    id: 'design-system',
    label: 'Design System',
    description: 'Live design-tokens & module-palet',
    icon: Palette
  },
  'support-content': {
    id: 'support-content',
    label: 'Ondersteuning',
    description: 'Uitleg & hints',
    icon: HelpCircle
  },
  'ldg-graph': {
    id: 'ldg-graph',
    label: 'Leerpad Graaf',
    description: 'Visuele LDG verkenner',
    icon: GitBranch
  },
  mascot: {
    id: 'mascot',
    label: 'Mascotte Djay',
    description: 'Djay design & preview',
    icon: Sparkles
  },
  diagnostics: {
    id: 'diagnostics',
    label: 'Diagnostiek',
    description: 'Seeds, taxonomie & console',
    icon: Wrench
  },
  logs: {
    id: 'logs',
    label: 'Activiteitenlog',
    description: 'Sessie logs',
    icon: Activity
  },
  settings: {
    id: 'settings',
    label: 'Instellingen',
    description: 'Adminvoorkeuren en systeemacties',
    icon: Wrench
  },
  analytics: {
    id: 'analytics',
    label: 'Sessie Analytics',
    description: 'Engine gedrag & telemetrie',
    icon: BarChart2
  }
};
export const ADMIN_NAV_GROUPS: readonly AdminNavGroup[] = [{
  label: 'Overzicht',
  items: [ADMIN_ROUTE_META.dashboard]
}, {
  label: 'Gebruikers',
  // Scholen staat bij Gebruikers en niet bij Systeem: het register bestaat om
  // een leerkracht te kunnen verifiëren, en dat is de handeling ernaast.
  items: [ADMIN_ROUTE_META.users, ADMIN_ROUTE_META.schools]
}, {
  label: 'Inhoud',
  items: [ADMIN_ROUTE_META['question-bank'], ADMIN_ROUTE_META.governance, ADMIN_ROUTE_META.ssot, ADMIN_ROUTE_META['code-registry'], ADMIN_ROUTE_META['design-system'], ADMIN_ROUTE_META['support-content'], ADMIN_ROUTE_META['ldg-graph'], ADMIN_ROUTE_META.mascot]
}, {
  label: 'Systeem',
  items: [ADMIN_ROUTE_META.analytics, ADMIN_ROUTE_META.diagnostics, ADMIN_ROUTE_META.logs]
}];
export function isAdminRoute(value: string): value is AdminRoute {
  return (ADMIN_ROUTE_IDS as readonly string[]).includes(value);
}
export function getAdminRouteMeta(route: AdminRoute): AdminNavItem {
  return ADMIN_ROUTE_META[route];
}
export function getAdminRouteLabel(route: AdminRoute): string {
  return getAdminRouteMeta(route).label;
}